import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import permissions from 'scoreboard/components/permissions'
import globals from 'scoreboard/components/globals'
import calendars from 'scoreboard/components/calendars'
import JsonStreamer from 'scoreboard/components/json-streamer'
import errorHandler from 'scoreboard/components/error-handler'
import reportsMeta from './reports-meta'
import ReportFolder from './report-folder'
import cloneDeepWithDatasetShallow from '../datasets/clone-deep-with-dataset-shallow'

const domainType = 'report'

var Report = Backbone.Model.extend({

	section: 'reports',
	domainType,

	chunkSize: 100,

	urlRoot: function(options) {
		return 'api/reports'
	},

	initialize: function(attributes, options) {
		this.options = options
	},

	isEditable() {
		return permissions.reports(this.get('organizationId')) && permissions.isEditableIfReplicated(this.toSubtype())
	},

	toSubtype: function(options) {
		var Model
		if (this.get('type') == 'folder') {
			Model = ReportFolder
		} else if (this.get('subtype') == 'unsupported-replicated-report') {
			return this
		} else {
			Model = reportsMeta[this.get('subtype')].model
		}

		//ensure new reports have their orgId recorded
		if (globals.orgId && !this.get('organizationId')) {
			this.set('organizationId', globals.orgId)
		}
		if (Model) {
			var opts = _.assign(this.options, options || {})
			var m = new Model(this.attributes, _.defaults(opts, {parse: true}))
			// #55557 don't cloneDeep the report, because it locks the CPU
			m.previousAttrs = cloneDeepWithDatasetShallow(m.attributes)
			return m
		}
		return this
	},

	parse: function(res, opts) {
		return res.metadata
	},

	fetch: function(options) {
		var opts = options || {}
		var success = opts.success
		opts.data = _.defaults(opts.data, {
			showCount: true,
			results: false,
			templateInfo: true
		}, defaultPeriodSettings())

		if (opts.data.results) {
			//ideally we'd just delegate to the subtype model but we have a chicken
			//and egg problem in that all we have is a report id, the subtype is
			//only known once retrieved from the server so we must handle the stream
			//here
			return this._streamResults(opts)
		} else {
			//intercept callee defined success, and convert to correct subtype
			//model to be passed back to the callee success
			opts.success = function(model, resp, opts) {
				if (success) {
					if (resp.form) {
						model.set(resp.form, {silent: true})
					}
					var m = model.toSubtype() || model
					let metaData = _.get(resp.form, 'metadata', {})
					let data = _.get(resp, 'results', [])
					m.stream && m.stream(data, { chunkSize: options?.data?.chunkSize })
					m._setMetadata && m._setMetadata(metaData)
					m.initialData = data
					success.call(opts.context, m, resp, opts)
				}
			}
			return Backbone.Model.prototype.fetch.call(this, opts)
		}
	},

	_streamResults: function(options) {
		var opts = options || {}
		var success = opts.success

		var stream = new JsonStreamer()
		// Listen using the model so listeners are cleaned up when the model is removed
		this.listenToOnce(stream, 'data', (key, value) => {
			//ensure the model id's is set
			this.id = value.id

			//set the meta data
			this.attributes = value

			//when fetching a report, this model is just a shell for the actual
			//subtype model which we can instantiate after the server tells us
			//what kind of report we're working with.
			this._model = this.toSubtype()
			this._model.stale = false
			this._model.initialData = []
			this._model.stream && this._model.stream(stream.chunker)
			this._model._setMetadata && this._model._setMetadata(value.metadata)
		})

		var def = $.Deferred()

		//resolve the deferred once data received or 'end' from stream processed
		var resolve = function() {
			if (def.state() != 'pending') return
			//pass along the real subtype model
			if (success) {
				success.call(opts.context, this._model, this._model.results, opts)
			}
			def.resolve(this._model)
		}.bind(this)

		this.listenToOnce(stream, 'stream', chunker => {
			//store reference to chunked stream to be added to the model that is
			//created after parsing the metadata returned
			stream.chunker = chunker
			chunker.on('data', chunk => {
				if (_.isEmpty(this._model.initialData)) {
					this._model.initialData = [].concat(chunk)
					resolve()
				} else {
					//#10935 add chunk to previously streamed results since reports
					//are cached unlike other domain objects
					this._model.initialData = this._model.initialData.concat(chunk)
				}
			})
			chunker.once('end', resolve)
		})

		let url
		let streamArgs = {}
		const state = options.data.state
		if (!_.isEmpty(state) && !!state.subtype) {
			//#45512 when exporting, run the report against the current state
			streamArgs.method = 'POST'
			streamArgs.data = JSON.stringify(state)
			url = this.urlRoot() + '/results'
		} else {
			//unlike jQuery.ajax, the underlying XhrStream (which defaults to a GET)
			//used in the streamer does not automatically append the data as req params
			//so we do it here
			url = opts.url || this.urlRoot() + '/' + this.get('id')
			if (opts.data) {
				url += '?' + $.param(opts.data)
			}
		}

		stream.start(_.defaults({
			url: url,
			stream: 'results',
			error: function(err) {
				if (opts.error) {
					opts.error(this, err.xhr, opts)
				} else {
					errorHandler(err.xhr)
				}
				def.reject(this, err.xhr)
			}.bind(this)
		}, streamArgs))
		return def
	}

})

/**
 * Returns the current period and calendar settings to use in a report
 */
function defaultPeriodSettings () {
	return {
		relativePeriodId: globals.periodId,
		currentCalendarId: _.isEmpty(calendars.calendars)
			? null
			: calendars.periodsById[globals.periodId].calendar.id
	}
}

export default Report
export { domainType }
export { defaultPeriodSettings }

Report.__filename__ = 'reports/report.js'
